/**
 * Single helper that propagates an address change across every record that holds
 * a copy of it: TimberProject, EnhancedDeal, and (when known) the originating Lead.
 *
 * The codebase used to copy addresses one-time at conversion (lead → deal → project)
 * and never re-sync. This function is the single re-sync point so a user can edit
 * the address from any surface (PDF viewer, project page, etc.) and have it land
 * everywhere consistently.
 *
 * Contacts and ClientPortals are intentionally NOT touched — those are billing
 * and contact records, not project location.
 */

import { db } from './firebase';
import { doc, getDoc, getDocs, query, where, collection, updateDoc, Timestamp } from 'firebase/firestore';
import type { Address, Coordinates } from '../types';
import { geocodeStructuredAddress } from './geocoding';
import { removeUndefinedDeep } from './removeUndefinedDeep';

const PROJECTS = 'timberProjects';
const DEALS = 'enhancedDeals';
const LEADS = 'leads';

export interface SetProjectAddressInput {
  address: Address;
  /** Display string shown in lists/headers. Usually `${street}, ${city}, ${state} ${zip}`. */
  location: string;
  /** When omitted, we will attempt to geocode automatically. */
  coordinates?: Coordinates;
}

export interface SetProjectAddressResult {
  projectId: string | null;
  dealId: string;
  leadId: string | null;
  geocoded: boolean;
  /** True if we wrote to all the records we found. */
  ok: boolean;
}

export interface ProjectAddressContext {
  projectId: string | null;
  dealId: string;
  leadId: string | null;
  /** The current address as best resolved from the project, then the deal, then the lead. */
  address?: Address;
  location?: string;
  coordinates?: Coordinates;
}

const EMPTY_ADDRESS: Address = {
  street: '',
  city: '',
  state: '',
  zipCode: '',
  country: 'United States',
};

/** True when the project has enough site info for maps / install weather. */
export function hasRecordedProjectSiteAddress(project: {
  location?: string;
  address?: Address;
} | null | undefined): boolean {
  if (!project) return false;
  if (project.location?.trim()) return true;
  const a = project.address;
  if (a?.street?.trim() || a?.city?.trim()) return true;
  return false;
}

export function emptyProjectAddress(): Address {
  return { ...EMPTY_ADDRESS };
}

/** Best-effort address + location from a timber project (or deal-shaped record). */
export function projectSiteAddressDraft(project: {
  location?: string;
  address?: Address;
  coordinates?: Coordinates;
}): { address: Address; location: string; coordinates?: Coordinates } {
  const address = project.address
    ? { ...emptyProjectAddress(), ...project.address }
    : emptyProjectAddress();
  const location =
    project.location?.trim() || (hasRecordedProjectSiteAddress({ address }) ? formatLocation(address) : '');
  return { address, location, coordinates: project.coordinates };
}

type ProjectSiteAddressSource = {
  location?: string;
  address?: Address;
  coordinates?: Coordinates;
} | null | undefined;

/** Prefer project site fields; fall back to the linked deal (matches Project Hub header). */
export function resolveProjectSiteAddressDraft(
  project: ProjectSiteAddressSource,
  deal?: ProjectSiteAddressSource,
): { address: Address; location: string; coordinates?: Coordinates } {
  const fromProject = project ? projectSiteAddressDraft(project) : projectSiteAddressDraft({});
  if (hasRecordedProjectSiteAddress(fromProject)) return fromProject;
  if (deal) {
    const fromDeal = projectSiteAddressDraft(deal);
    if (hasRecordedProjectSiteAddress(fromDeal)) return fromDeal;
  }
  return fromProject;
}

/** Cohesive `{ address, location }` for supplier RFQ ship-to resolution. */
export function projectSiteInputFromProjectAndDeal(
  project: ProjectSiteAddressSource,
  deal?: ProjectSiteAddressSource,
): { address: Address; location: string } {
  const draft = resolveProjectSiteAddressDraft(project, deal);
  return { address: draft.address, location: draft.location };
}

export function projectSiteAddressDraftsEqual(
  a: { address: Address; location: string; coordinates?: Coordinates },
  b: { address: Address; location: string; coordinates?: Coordinates },
): boolean {
  return (
    a.location.trim() === b.location.trim() &&
    JSON.stringify(a.address) === JSON.stringify(b.address) &&
    JSON.stringify(a.coordinates ?? null) === JSON.stringify(b.coordinates ?? null)
  );
}

/** Build a location string from a structured Address. */
export function formatLocation(addr: Address): string {
  const parts: string[] = [];
  if (addr.street) parts.push(addr.street);
  const cityState = [addr.city, addr.state].filter(Boolean).join(', ');
  if (cityState) parts.push(cityState);
  if (addr.zipCode) parts.push(addr.zipCode);
  return parts.filter(Boolean).join(', ');
}

/** Find the project + lead linked to a given deal id. */
async function resolveContext(dealId: string): Promise<{ projectId: string | null; leadId: string | null }> {
  // Project: timberProjects.dealId == dealId.
  const projSnap = await getDocs(query(collection(db, PROJECTS), where('dealId', '==', dealId)));
  const projectId = projSnap.empty ? null : projSnap.docs[0].id;

  // Lead: deal.leadId.
  const dealSnap = await getDoc(doc(db, DEALS, dealId));
  const dealData = dealSnap.exists() ? dealSnap.data() : null;
  const leadId = (dealData?.leadId as string | undefined) || null;

  return { projectId, leadId };
}

/** Read the current address from project (preferred), deal, or lead. */
export async function getProjectAddressForDeal(dealId: string): Promise<ProjectAddressContext | null> {
  if (!dealId) return null;
  const { projectId, leadId } = await resolveContext(dealId);

  let address: Address | undefined;
  let location: string | undefined;
  let coordinates: Coordinates | undefined;

  const pull = (data: Record<string, unknown> | null) => {
    if (!data) return false;
    let any = false;
    if (data.address && typeof data.address === 'object') {
      address = data.address as Address;
      any = true;
    }
    if (typeof data.location === 'string' && data.location) {
      location = data.location;
      any = true;
    }
    if (data.coordinates && typeof data.coordinates === 'object') {
      coordinates = data.coordinates as Coordinates;
      any = true;
    }
    return any;
  };

  if (projectId) {
    const snap = await getDoc(doc(db, PROJECTS, projectId));
    if (snap.exists()) pull(snap.data());
  }

  // Fall back to the deal if any of the project fields are missing.
  if (!address || !location) {
    const dealSnap = await getDoc(doc(db, DEALS, dealId));
    if (dealSnap.exists()) {
      const data = dealSnap.data();
      if (!address && data.address) address = data.address as Address;
      if (!location && typeof data.location === 'string') location = data.location;
      if (!coordinates && data.coordinates) coordinates = data.coordinates as Coordinates;
    }
  }

  if (!address && leadId) {
    const leadSnap = await getDoc(doc(db, LEADS, leadId));
    if (leadSnap.exists()) {
      const data = leadSnap.data();
      if (!address && data.address) address = data.address as Address;
      if (!location && typeof data.location === 'string') location = data.location;
      if (!coordinates && data.coordinates) coordinates = data.coordinates as Coordinates;
    }
  }

  return {
    projectId,
    dealId,
    leadId,
    address,
    location,
    coordinates,
  };
}

/**
 * Write the supplied address to the TimberProject (when present), the EnhancedDeal,
 * and the originating Lead (when known). Geocodes automatically if coordinates not
 * supplied. Geocoding failure is non-fatal — the text fields still propagate.
 */
export async function setProjectAddressEverywhere(
  dealId: string,
  next: SetProjectAddressInput,
): Promise<SetProjectAddressResult> {
  if (!dealId) throw new Error('dealId is required');

  let coordinates = next.coordinates;
  let geocoded = false;
  if (!coordinates) {
    try {
      const geo = await geocodeStructuredAddress(next.address);
      if (geo) {
        coordinates = geo;
        geocoded = true;
      }
    } catch (err) {
      console.warn('Address geocoding failed (non-fatal):', err);
    }
  }

  const { projectId, leadId } = await resolveContext(dealId);
  const now = Timestamp.now();
  const payload = removeUndefinedDeep({
    address: next.address,
    location: next.location,
    coordinates,
    updatedAt: now,
  });

  const writes: Promise<unknown>[] = [];
  if (projectId) writes.push(updateDoc(doc(db, PROJECTS, projectId), payload));
  writes.push(updateDoc(doc(db, DEALS, dealId), payload));
  if (leadId) writes.push(updateDoc(doc(db, LEADS, leadId), payload));

  let ok = true;
  try {
    await Promise.all(writes);
  } catch (err) {
    console.error('Failed to propagate address to one or more records:', err);
    ok = false;
    throw err;
  }

  return { projectId, dealId, leadId, geocoded: !!coordinates && geocoded, ok };
}
